// apps/web/src/lib/painel/home/saude.ts — Fase 17.8, Plano 21 (D-03; UI-SPEC Copywriting "Saúde do sistema", U-22).
// Textos e LEDs da faixa "Saúde do sistema" do Admin. O status é SEMPRE texto (a cor do LED nunca é o único sinal); item sem fonte
// ("Sem informação agora") tem LED neutro e não afeta os outros.
import type { SaudeSistema } from '@pieltec/contracts';
import { diaMesSaoPaulo, diasCivisAtras, formatarNumero, horaSaoPaulo, tempoRelativo } from './formato';

export const TEXTO_SEM_INFORMACAO = 'Sem informação agora';
export const TEXTO_VERIFIQUE = 'verifique se está normal';
export const TEXTO_VERIFIQUE_CURTO = 'verifique';

/** Passou desta idade sem sincronizar, o Omie pede atenção (mesmo padrão da captação de NF, que avisa depois de 26 h). */
export const LIMITE_HORAS_SYNC_OMIE = 24;

export type IdItemSaude = 'omie' | 'usuarios' | 'captacaoNf' | 'perfis';
export type LedSaude = 'normal' | 'atencao' | 'neutro';

type Medido<T> = Extract<T, { estado: 'MEDIDO' }>;
type DadosOmie = Medido<SaudeSistema['omie']>;
type DadosUsuarios = Medido<SaudeSistema['usuarios']>;
type DadosCaptacao = Medido<SaudeSistema['captacaoNf']>;
type DadosPerfis = Medido<SaudeSistema['perfis']>;

const NOMES: Readonly<Record<IdItemSaude, string>> = {
  omie: 'Omie',
  usuarios: 'Usuários ativos agora',
  captacaoNf: 'Captação de NF de entrada',
  perfis: 'Perfis e permissões',
};

const ORDEM: readonly IdItemSaude[] = ['omie', 'usuarios', 'captacaoNf', 'perfis'];

const plural = (n: number, singular: string, pluralTexto: string): string => (n === 1 ? singular : pluralTexto);

/** Quantidade sem ação / tablet / alteração: "nenhuma" no feminino ou masculino no lugar do 0. */
const quantidade = (n: number, zero: string, singular: string, pluralTexto: string): string => (n === 0 ? zero : `${formatarNumero(n)} ${plural(n, singular, pluralTexto)}`);

/** A última sincronização passou de `LIMITE_HORAS_SYNC_OMIE` horas? Sem data (ou ilegível) não há o que comparar: falso. */
export function sincronizacaoOmieAtrasada(ultimaSincronizacao: string | null, agora: Date): boolean {
  if (ultimaSincronizacao === null) return false;
  const instante = Date.parse(ultimaSincronizacao);
  if (Number.isNaN(instante)) return false;
  return agora.getTime() - instante > LIMITE_HORAS_SYNC_OMIE * 3_600_000;
}

/** "hoje às 14:02", "ontem às 14:02" ou "em 26/08 às 14:02" (dia civil e hora de São Paulo); instante ilegível não vira texto. */
function quandoSincronizou(iso: string, agora: Date): string | undefined {
  const hora = horaSaoPaulo(iso);
  const diaMes = diaMesSaoPaulo(iso);
  if (hora === undefined || diaMes === undefined) return undefined;
  const dias = diasCivisAtras(new Date(Date.parse(iso)), agora);
  if (dias === 0) return `hoje às ${hora}`;
  if (dias === 1) return `ontem às ${hora}`;
  return `em ${diaMes} às ${hora}`;
}

/**
 * "sincronizado hoje às 14:02 · 1 ação necessária" (hora de São Paulo); fora do dia mostra "ontem" ou a data ("em 26/08 às 13:34") e,
 * passado o limite de `LIMITE_HORAS_SYNC_OMIE` h, termina com "· verifique".
 */
export function textoOmie(dados: { readonly ultimaSincronizacao: string | null; readonly acoesNecessarias: number }, agora: Date): string {
  const quando = dados.ultimaSincronizacao === null ? undefined : quandoSincronizou(dados.ultimaSincronizacao, agora);
  const acoes = quantidade(dados.acoesNecessarias, 'nenhuma ação necessária', 'ação necessária', 'ações necessárias');
  const base = `${quando === undefined ? 'sem sincronização registrada' : `sincronizado ${quando}`} · ${acoes}`;
  return sincronizacaoOmieAtrasada(dados.ultimaSincronizacao, agora) ? `${base} · ${TEXTO_VERIFIQUE_CURTO}` : base;
}

/** "14 de 37 · 6 tablets apontando". */
export function textoUsuarios(dados: { readonly ativosAgora: number; readonly total: number; readonly tabletsApontando: number; readonly janelaMin?: number }): string {
  const tablets = dados.tabletsApontando === 0 ? 'nenhum tablet apontando' : `${formatarNumero(dados.tabletsApontando)} ${plural(dados.tabletsApontando, 'tablet', 'tablets')} apontando`;
  return `${formatarNumero(dados.ativosAgora)} de ${formatarNumero(dados.total)} · ${tablets}`;
}

/** "última nota há 2 h" e, quando passou do limite, "· verifique se está normal". Sem nota captada: texto neutro. */
export function textoCaptacao(dados: { readonly ultimaNotaEm: string | null; readonly verificar: boolean }, agora: Date): string {
  if (dados.ultimaNotaEm === null) return 'nenhuma nota captada ainda';
  const instante = Date.parse(dados.ultimaNotaEm);
  const base = Number.isNaN(instante) ? 'última nota sem data' : `última nota ${tempoRelativo(new Date(instante), agora)}`;
  return dados.verificar ? `${base} · ${TEXTO_VERIFIQUE}` : base;
}

/** "1 alteração nesta semana". */
export function textoPerfis(alteracoes: number): string {
  return `${quantidade(alteracoes, 'nenhuma alteração', 'alteração', 'alterações')} nesta semana`;
}

/**
 * LED do item: `atencao` com ação necessária no Omie, Omie sem sincronizar há mais de `LIMITE_HORAS_SYNC_OMIE` h ou "verificar" na captação; `neutro` sem informação (indisponível, sem medição ou
 * sem registro nenhum para comparar); `normal` nos demais.
 */
export function ledDoItem(id: IdItemSaude, item: SaudeSistema[IdItemSaude], agora: Date): LedSaude {
  if (item.estado !== 'MEDIDO') return 'neutro';
  if (id === 'omie') {
    const omie = item as DadosOmie;
    if (omie.acoesNecessarias > 0 || sincronizacaoOmieAtrasada(omie.ultimaSincronizacao, agora)) return 'atencao';
    return omie.ultimaSincronizacao === null ? 'neutro' : 'normal';
  }
  if (id === 'captacaoNf') {
    const captacao = item as DadosCaptacao;
    if (captacao.verificar) return 'atencao';
    return captacao.ultimaNotaEm === null ? 'neutro' : 'normal';
  }
  return 'normal';
}

export interface ItemSaudeDescrito {
  readonly id: IdItemSaude;
  readonly nome: string;
  readonly texto: string;
  readonly led: LedSaude;
}

/** Os 4 itens na ordem fixa, cada um com nome, texto de status e LED. */
export function descreverItensDaSaude(saude: SaudeSistema, agora: Date): readonly ItemSaudeDescrito[] {
  return ORDEM.map((id): ItemSaudeDescrito => {
    const item = saude[id];
    const base = { id, nome: NOMES[id], led: ledDoItem(id, item, agora) };
    if (item.estado !== 'MEDIDO') return { ...base, texto: TEXTO_SEM_INFORMACAO };
    switch (id) {
      case 'omie':
        return { ...base, texto: textoOmie(saude.omie as DadosOmie, agora) };
      case 'usuarios':
        return { ...base, texto: textoUsuarios(saude.usuarios as DadosUsuarios) };
      case 'captacaoNf':
        return { ...base, texto: textoCaptacao(saude.captacaoNf as DadosCaptacao, agora) };
      case 'perfis':
        return { ...base, texto: textoPerfis((saude.perfis as DadosPerfis).alteracoes7d) };
    }
  });
}
